import type { FunctionComponent, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { useChartSize } from '~/ui/core/charts/use-chart-canvas';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import type { AutomationHandoverDestination, AutomationHandoverResponse } from '../../data-contract/automation';
import { AUTOMATION_HANDOVER_DESTINATIONS } from '../../data-contract/automation';
import { usePalette } from '../components/PaletteProvider';
import { useLocale } from '../components/use-locale';
import { formatPercent } from '../model/automation';
import type { SankeyFlow } from './sankey-layout';
import { layoutSankey, ribbonPath, stackLabels } from './sankey-layout';
import { useChartColors } from './use-chart-colors';

const HEIGHT = 420;
const NODE_WIDTH = 12;
const NODE_GAP = 10;
const LABEL_HEIGHT = 34;
const LEFT_GUTTER = 150;
const RIGHT_GUTTER = 230;
const DIMMED_OPACITY = 0.18;
const RIBBON_OPACITY = 0.75;
const RETAINED_OPACITY = 0.55;
const SOURCES = ['contribution', 'commodity', 'unclassified'] as const;
type Source = (typeof SOURCES)[number];

const SOURCE_KEY = {
    commodity: 'pages.ai-transformation.handover-chart.source-commodity',
    contribution: 'pages.ai-transformation.handover-chart.source-contribution',
    unclassified: 'pages.ai-transformation.handover-chart.source-unclassified',
} as const;

const sourceOf = (classification: string | null | undefined): Source =>
    classification === 'contribution' || classification === 'commodity' ? classification : 'unclassified';

const HandoverTooltip: FunctionComponent<{
    readonly children: ReactNode;
    readonly label: string;
    readonly testId: string;
    readonly onOpenChange: (open: boolean) => void;
}> = ({ children, label, testId, onOpenChange }) => (
    <Tooltip disableHoverableContent={true} onOpenChange={onOpenChange}>
        <Tooltip.Trigger asChild={true}>
            <g aria-label={label} data-testid={testId} tabIndex={0}>
                {children}
            </g>
        </Tooltip.Trigger>
        <Tooltip.Portal>
            <Tooltip.Content side="top" sideOffset={8}>
                {label}
            </Tooltip.Content>
        </Tooltip.Portal>
    </Tooltip>
);

export const WorkHandover: FunctionComponent<{
    readonly data: Pick<AutomationHandoverResponse, 'flows' | 'assessedItemCount' | 'itemCount'>;
    readonly compact?: boolean;
}> = ({ data, compact = false }) => {
    const height = compact ? 160 : HEIGHT;
    const translate = useTranslate();
    const locale = useLocale();
    const { bandColor } = usePalette();
    const { axis, ink, inkFaint } = useChartColors();
    const [containerRef, size] = useChartSize(height);
    const [hoveredKey, setHoveredKey] = useState<string | undefined>(undefined);

    const flows = useMemo<readonly SankeyFlow<Source, AutomationHandoverDestination>[]>(
        () =>
            data.flows.map((flow) => ({
                destination: flow.destination,
                share: flow.share,
                source: sourceOf(flow.classification),
            })),
        [data.flows],
    );

    const layout = useMemo(
        () => layoutSankey(flows, SOURCES, AUTOMATION_HANDOVER_DESTINATIONS, { gap: NODE_GAP, height }),
        [flows, height],
    );

    const destinationLabel = (destination: AutomationHandoverDestination): string =>
        destination === 'RELEASED'
            ? translate('pages.ai-transformation.handover-chart.destination-released')
            : translate('pages.ai-transformation.handover-chart.destination-retained');

    const colorOf = (destination: AutomationHandoverDestination): string =>
        destination === 'RELEASED' ? bandColor('HIGH') : ink;

    const opacityOf = (destination: AutomationHandoverDestination): number =>
        destination === 'RELEASED' ? RIBBON_OPACITY : RETAINED_OPACITY;

    const leftX = compact ? 0 : LEFT_GUTTER;
    const rightX = Math.max(leftX + NODE_WIDTH * 4, size.width - (compact ? NODE_WIDTH : RIGHT_GUTTER));
    const sourceLabelTops = stackLabels(
        layout.sources.map((node) => (node.y0 + node.y1) / 2),
        LABEL_HEIGHT,
        height,
    );
    const destinationLabelTops = stackLabels(
        layout.destinations.map((node) => (node.y0 + node.y1) / 2),
        LABEL_HEIGHT,
        height,
    );

    if (data.flows.length === 0) {
        return (
            <div ref={containerRef} className="flex flex-col gap-m" data-testid="ai-transformation-work-handover">
                <EmptyStateMessage>{translate('pages.ai-transformation.handover-chart.empty')}</EmptyStateMessage>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="flex min-w-0 flex-col gap-s rounded-lg bg-surface-hover p-m"
            data-testid="ai-transformation-work-handover"
        >
            <div className="flex items-baseline justify-between gap-m text-xs text-text-content-subtle">
                <span>{translate('pages.ai-transformation.handover-chart.title-sources')}</span>
                <span>{translate('pages.ai-transformation.handover-chart.title-destinations')}</span>
            </div>
            <div className="relative w-full overflow-hidden">
                <svg
                    aria-label={translate('pages.ai-transformation.questions.how-is-work-handed-over.title')}
                    data-testid="ai-transformation-work-handover-svg"
                    height={height}
                    role="img"
                    viewBox={`0 0 ${size.width} ${height}`}
                    width="100%"
                    onPointerLeave={() => setHoveredKey(undefined)}
                >
                    {layout.ribbons.map((ribbon) => {
                        const key = `${ribbon.source} ${ribbon.destination}`;

                        return (
                            <HandoverTooltip
                                key={key}
                                label={translate('pages.ai-transformation.handover-chart.flow-label', {
                                    destination: destinationLabel(ribbon.destination),
                                    share: formatPercent(ribbon.share, locale),
                                    source: translate(SOURCE_KEY[ribbon.source]),
                                })}
                                testId="ai-transformation-handover-flow"
                                onOpenChange={(open) => setHoveredKey(open ? key : undefined)}
                            >
                                <path
                                    d={ribbonPath(leftX + NODE_WIDTH, rightX, ribbon)}
                                    fill={colorOf(ribbon.destination)}
                                    opacity={
                                        hoveredKey === undefined ||
                                        hoveredKey === key ||
                                        hoveredKey === ribbon.source ||
                                        hoveredKey === ribbon.destination
                                            ? opacityOf(ribbon.destination)
                                            : DIMMED_OPACITY
                                    }
                                />
                            </HandoverTooltip>
                        );
                    })}
                    {layout.sources.map((node, index) => {
                        const radius = Math.min(3, (node.y1 - node.y0) / 2);
                        return (
                            <g key={node.id}>
                                <HandoverTooltip
                                    label={`${translate(SOURCE_KEY[node.id])} · ${formatPercent(node.share, locale)}`}
                                    testId="ai-transformation-handover-source"
                                    onOpenChange={(open) => setHoveredKey(open ? node.id : undefined)}
                                >
                                    <path
                                        fill={axis}
                                        d={[
                                            `M${leftX + NODE_WIDTH},${node.y0}`,
                                            `H${leftX + radius}`,
                                            `Q${leftX},${node.y0} ${leftX},${node.y0 + radius}`,
                                            `V${node.y1 - radius}`,
                                            `Q${leftX},${node.y1} ${leftX + radius},${node.y1}`,
                                            `H${leftX + NODE_WIDTH}`,
                                            'Z',
                                        ].join(' ')}
                                    />
                                </HandoverTooltip>
                                {compact ? null : (
                                    <>
                                        <text
                                            fill={ink}
                                            fontSize={12}
                                            textAnchor="end"
                                            x={leftX - 10}
                                            y={(sourceLabelTops[index] ?? node.y0) + 12}
                                        >
                                            {translate(SOURCE_KEY[node.id])}
                                        </text>
                                        <text
                                            fill={inkFaint}
                                            fontSize={11}
                                            textAnchor="end"
                                            x={leftX - 10}
                                            y={(sourceLabelTops[index] ?? node.y0) + 27}
                                        >
                                            {formatPercent(node.share, locale)}
                                        </text>
                                    </>
                                )}
                            </g>
                        );
                    })}
                    {layout.destinations.map((node, index) => {
                        const radius = Math.min(3, (node.y1 - node.y0) / 2);
                        return (
                            <g key={node.id}>
                                <HandoverTooltip
                                    label={`${destinationLabel(node.id)} · ${formatPercent(node.share, locale)}`}
                                    testId="ai-transformation-handover-destination"
                                    onOpenChange={(open) => setHoveredKey(open ? node.id : undefined)}
                                >
                                    <path
                                        fill={colorOf(node.id)}
                                        opacity={opacityOf(node.id)}
                                        d={[
                                            `M${rightX},${node.y0}`,
                                            `H${rightX + NODE_WIDTH - radius}`,
                                            `Q${rightX + NODE_WIDTH},${node.y0} ${rightX + NODE_WIDTH},${node.y0 + radius}`,
                                            `V${node.y1 - radius}`,
                                            `Q${rightX + NODE_WIDTH},${node.y1} ${rightX + NODE_WIDTH - radius},${node.y1}`,
                                            `H${rightX}`,
                                            'Z',
                                        ].join(' ')}
                                    />
                                </HandoverTooltip>
                                {compact ? null : (
                                    <>
                                        <text
                                            fill={ink}
                                            fontSize={12}
                                            textAnchor="start"
                                            x={rightX + NODE_WIDTH + 10}
                                            y={(destinationLabelTops[index] ?? node.y0) + 12}
                                        >
                                            {destinationLabel(node.id)}
                                        </text>
                                        <text
                                            fill={inkFaint}
                                            fontSize={11}
                                            textAnchor="start"
                                            x={rightX + NODE_WIDTH + 10}
                                            y={(destinationLabelTops[index] ?? node.y0) + 27}
                                        >
                                            {formatPercent(node.share, locale)}
                                        </text>
                                    </>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
            {compact ? (
                <div className="grid grid-cols-2 gap-m text-xxs text-text-secondary">
                    <div className="flex flex-col gap-xs">
                        {layout.sources.map((node) => (
                            <span key={node.id}>
                                {translate(SOURCE_KEY[node.id])} · {formatPercent(node.share, locale)}
                            </span>
                        ))}
                    </div>
                    <div className="flex flex-col gap-xs text-right">
                        {layout.destinations.map((node) => (
                            <span key={node.id}>
                                {destinationLabel(node.id)} · {formatPercent(node.share, locale)}
                            </span>
                        ))}
                    </div>
                </div>
            ) : (
                <span className="text-xxs text-text-muted">
                    {translate('pages.ai-transformation.handover-chart.items-note', {
                        assessed: data.assessedItemCount,
                        total: data.itemCount,
                    })}
                </span>
            )}
        </div>
    );
};
